import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiUserX,
  FiUsers,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import useAuth
  from "../../../../../../hooks/useAuth";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeePageHeader
  from "../common/EmployeePageHeader";

import EmployeeModuleTabs
  from "../common/EmployeeModuleTabs";

import EmployeeSelector
  from "../common/EmployeeSelector";

import EmployeeConfirmModal
  from "../common/EmployeeConfirmModal";

import EmployeeAttendanceTable
  from "./EmployeeAttendanceTable";

import BulkAttendanceModal
  from "./BulkAttendanceModal";

import AttendanceDetailsDrawer
  from "./AttendanceDetailsDrawer";

import {
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeAttendance.css";


const getToday =
  () =>
    new Date()
      .toISOString()
      .slice(
        0,
        10
      );


const normalizeRoleCode = (
  value
) =>
  String(
    value ||
    ""
  )
    .trim()
    .toUpperCase();


const INITIAL_FILTERS = {
  employeeId: "",
  dateFrom: getToday(),
  dateTo: getToday(),
  status: "",
  search: "",
};


const INITIAL_PAGINATION = {
  page: 1,
  limit: 20,
  totalRecords: 0,
  totalPages: 1,
};


const INITIAL_SUMMARY = {
  totalEmployees: 0,
  present: 0,
  absent: 0,
  halfDay: 0,
  paidLeave: 0,
  unpaidLeave: 0,
  holiday: 0,
  weekOff: 0,
};


const EmployeeAttendance = () => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  /*
  |--------------------------------------------------------------------------
  | AUTH / ATTENDANCE SCOPE
  |--------------------------------------------------------------------------
  */

  const {
    user,
    role,
    isOwner,
  } =
    useAuth();


  const normalizedUserType =
    normalizeRoleCode(
      user?.userType
    );


  const normalizedRole =
    normalizeRoleCode(
      role?.code ||
      user?.role?.code ||
      user?.roleCode
    );


  /*
  |--------------------------------------------------------------------------
  | ATTENDANCE ACCESS MODE
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | EMPLOYEE userType is always SELF ONLY.
  |
  | This intentionally wins over role.code so an employee account cannot
  | accidentally receive the tenant-wide employee selector because of a
  | legacy/default ADMIN-like role code.
  |
  |--------------------------------------------------------------------------
  */

  /*
  |--------------------------------------------------------------------------
  | MANAGER / SELF ATTENDANCE MODE
  |--------------------------------------------------------------------------
  |
  | Manager access must win first.
  |
  | In some existing tenant accounts user.userType can still contain a legacy
  | EMPLOYEE-like value even when AuthProvider correctly identifies the login
  | as OWNER through isOwner / role.code.
  |
  | Previous logic checked EMPLOYEE first, so a valid OWNER could be forced
  | into self-only mode and see a locked selector.
  |
  |--------------------------------------------------------------------------
  */

  const isAttendanceManager =
    Boolean(
      isOwner
    ) ||
    normalizedUserType ===
      "OWNER" ||
    normalizedUserType ===
      "ADMIN" ||
    normalizedRole ===
      "OWNER" ||
    normalizedRole ===
      "ADMIN";


  const isEmployeeLogin =
    !isAttendanceManager &&
    normalizedUserType ===
      "EMPLOYEE";


  /*
  |--------------------------------------------------------------------------
  | DATA
  |--------------------------------------------------------------------------
  */

  const [
    records,
    setRecords,
  ] =
    useState([]);


  const [
    employees,
    setEmployees,
  ] =
    useState([]);


  const [
    summary,
    setSummary,
  ] =
    useState({
      ...INITIAL_SUMMARY,
    });


  /*
  |--------------------------------------------------------------------------
  | FILTER
  |--------------------------------------------------------------------------
  */

  const [
    filters,
    setFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  /*
  |--------------------------------------------------------------------------
  | PAGINATION
  |--------------------------------------------------------------------------
  */

  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...INITIAL_PAGINATION,
    });


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    employeeLoading,
    setEmployeeLoading,
  ] =
    useState(false);


  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | SELECTED
  |--------------------------------------------------------------------------
  */

  const [
    selectedAttendance,
    setSelectedAttendance,
  ] =
    useState(null);


  /*
  |--------------------------------------------------------------------------
  | MODALS
  |--------------------------------------------------------------------------
  */

  const [
    bulkOpen,
    setBulkOpen,
  ] =
    useState(false);


  const [
    detailsOpen,
    setDetailsOpen,
  ] =
    useState(false);


  const [
    deleteOpen,
    setDeleteOpen,
  ] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    useCallback(
      async () => {

        try {

          setEmployeeLoading(
            true
          );


          if (
            isAttendanceManager
          ) {

            const response =
              await api.post(
                config.getEmployees,
                {
                  page: 1,
                  limit: 500,
                  status: "ACTIVE",
                }
              );


            if (
              response?.status !==
              "success"
            ) {

              throw new Error(
                response?.message ||
                "Unable to load employees."
              );

            }


            setEmployees(
              Array.isArray(
                response?.data
              )
                ?
                response.data
                :
                []
            );


            return;

          }


          /*
           * Employee login:
           * do NOT request the tenant-wide employee list.
           */
          const response =
            await api.get(
              config.getMyAttendanceStatus
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load your employee profile."
            );

          }


          const currentEmployee =
            response?.data?.employee ||
            null;


          const employeeId =
            currentEmployee?._id ||
            "";


          setEmployees(
            currentEmployee
              ?
              [
                currentEmployee,
              ]
              :
              []
          );


          setFilters(
            (
              previous
            ) => ({
              ...previous,

              employeeId,

              search:
                "",
            })
          );


          setAppliedFilters(
            (
              previous
            ) => ({
              ...previous,

              employeeId,

              search:
                "",
            })
          );

        } catch (
          requestError
        ) {

          console.error(
            "Attendance employee scope error:",
            requestError
          );


          setEmployees(
            []
          );


          if (
            !isAttendanceManager
          ) {

            setError(
              getEmployeeApiError(
                requestError,
                "Unable to resolve your employee attendance profile."
              )
            );

          }

        } finally {

          setEmployeeLoading(
            false
          );

        }

      },
      [
        api,
        isAttendanceManager,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD ATTENDANCE
  |--------------------------------------------------------------------------
  */

  const loadAttendance =
    useCallback(
      async () => {

        try {

          setLoading(
            true
          );


          setError(
            ""
          );


          const response =
            await api.post(
              config.getEmployeeAttendance,
              {
                page:
                  pagination.page,

                limit:
                  pagination.limit,

                employeeId:
                  appliedFilters.employeeId,

                dateFrom:
                  appliedFilters.dateFrom,

                dateTo:
                  appliedFilters.dateTo,

                status:
                  appliedFilters.status,

                search:
                  appliedFilters.search,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load attendance."
            );

          }


          const data =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              [];


          setRecords(
            data
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              totalRecords:
                Number(
                  response
                    ?.pagination
                    ?.totalRecords ??
                  data.length
                ),

              totalPages:
                Math.max(
                  Number(
                    response
                      ?.pagination
                      ?.totalPages
                  ) ||
                  1,
                  1
                ),
            })
          );


          /*
          |--------------------------------------------------------------------------
          | SUMMARY FROM API
          |--------------------------------------------------------------------------
          */

          if (
            response?.summary
          ) {

            setSummary({
              ...INITIAL_SUMMARY,
              ...response.summary,
            });


            return;

          }


          /*
          |--------------------------------------------------------------------------
          | FALLBACK SUMMARY
          |--------------------------------------------------------------------------
          */

          const calculated = {
            ...INITIAL_SUMMARY,

            totalEmployees:
              data.length,
          };


          data.forEach(
            (
              item
            ) => {

              const status =
                String(
                  item?.status ||
                  ""
                )
                  .trim()
                  .toUpperCase();


              if (
                status ===
                "PRESENT"
              ) {

                calculated.present +=
                  1;

              }


              if (
                status ===
                "ABSENT"
              ) {

                calculated.absent +=
                  1;

              }


              if (
                status ===
                "HALF_DAY"
              ) {

                calculated.halfDay +=
                  1;

              }


              if (
                status ===
                "PAID_LEAVE"
              ) {

                calculated.paidLeave +=
                  1;

              }


              if (
                status ===
                "UNPAID_LEAVE"
              ) {

                calculated.unpaidLeave +=
                  1;

              }


              if (
                status ===
                "HOLIDAY"
              ) {

                calculated.holiday +=
                  1;

              }


              if (
                status ===
                "WEEK_OFF"
              ) {

                calculated.weekOff +=
                  1;

              }

            }
          );


          setSummary(
            calculated
          );

        } catch (
          requestError
        ) {

          console.error(
            "Attendance list error:",
            requestError
          );


          setRecords(
            []
          );


          setError(
            getEmployeeApiError(
              requestError,
              "Unable to load attendance."
            )
          );

        } finally {

          setLoading(
            false
          );

        }

      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      loadEmployees();

    },
    [
      loadEmployees,
    ]
  );


  useEffect(
    () => {

      loadAttendance();

    },
    [
      loadAttendance,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const handleSearch =
    () => {

      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page: 1,
        })
      );


      setAppliedFilters({
        ...filters,

        search:
          String(
            filters.search ||
            ""
          ).trim(),
      });

    };


  /*
  |--------------------------------------------------------------------------
  | RESET
  |--------------------------------------------------------------------------
  */

  const handleReset =
    () => {

      const selfEmployeeId =
        !isAttendanceManager
          ?
          employees?.[0]?._id ||
          appliedFilters.employeeId ||
          ""
          :
          "";


      const nextFilters = {
        ...INITIAL_FILTERS,

        employeeId:
          selfEmployeeId,

        search:
          "",
      };


      setFilters(
        nextFilters
      );


      setAppliedFilters(
        nextFilters
      );


      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page: 1,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS
  |--------------------------------------------------------------------------
  */

  const handleSuccess =
    async () => {

      setBulkOpen(
        false
      );


      setDetailsOpen(
        false
      );


      setDeleteOpen(
        false
      );


      setSelectedAttendance(
        null
      );


      await loadAttendance();

    };


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete =
    async () => {

      if (
        !selectedAttendance?._id
      ) {

        return;

      }


      try {

        setActionLoading(
          true
        );


        const response =
          await api.delete(
            config.deleteEmployeeAttendance(
              selectedAttendance._id
            )
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to delete attendance."
          );

        }


        await handleSuccess();

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to delete attendance."
          )
        );

      } finally {

        setActionLoading(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | SUMMARY
  |--------------------------------------------------------------------------
  */

  const summaryCards = [

    {
      label:
        isAttendanceManager
          ?
          "Total Employees"
          :
          "My Records",

      value:
        summary.totalEmployees,

      icon:
        FiUsers,

      type:
        "blue",
    },

    {
      label:
        "Present",

      value:
        summary.present,

      icon:
        FiCheckCircle,

      type:
        "green",
    },

    {
      label:
        "Absent",

      value:
        summary.absent,

      icon:
        FiUserX,

      type:
        "red",
    },

    {
      label:
        "Half Day",

      value:
        summary.halfDay,

      icon:
        FiClock,

      type:
        "orange",
    },

    {
      label:
        "On Leave",

      value:
        Number(
          summary.paidLeave ||
          0
        ) +
        Number(
          summary.unpaidLeave ||
          0
        ),

      icon:
        FiCalendar,

      type:
        "purple",
    },

  ];


  return (

    <div className="employee-attendance-page">

      <EmployeePageHeader
        title="Employee Attendance"
        description="Manage daily attendance, working time, leave attendance and payroll attendance records."
        actionLabel={
          isAttendanceManager
            ?
            "Mark Attendance"
            :
            null
        }
        actionIcon={
          isAttendanceManager
            ?
            <FiPlus />
            :
            null
        }
        onAction={
          isAttendanceManager
            ?
            () =>
              setBulkOpen(
                true
              )
            :
            undefined
        }
        onRefresh={
          loadAttendance
        }
        loading={
          loading
        }
      />


      <EmployeeModuleTabs />


      <div className="employee-attendance-summary-grid">

        {
          summaryCards.map(
            (
              card
            ) => {

              const Icon =
                card.icon;


              return (

                <article
                  key={
                    card.label
                  }
                  className={`employee-attendance-summary-card employee-attendance-summary-card--${card.type}`}
                >

                  <span>

                    <Icon />

                  </span>


                  <div>

                    <small>
                      {card.label}
                    </small>

                    <strong>
                      {
                        Number(
                          card.value ||
                          0
                        )
                      }
                    </strong>

                  </div>

                </article>

              );

            }
          )
        }

      </div>


      <section className="employee-attendance-filter-card">

        <div className="employee-attendance-filter-grid">

          <EmployeeSelector
            employees={
              employees
            }
            value={
              filters.employeeId
            }
            onChange={
              isAttendanceManager
                ?
                (
                  employeeId
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      employeeId,
                    })
                  )
                :
                undefined
            }
            label="Employee"
            placeholder={
              isAttendanceManager
                ?
                "All Employees"
                :
                "My Attendance"
            }
            disabled={
              employeeLoading ||
              !isAttendanceManager
            }
            locked={
              !isAttendanceManager
            }
            showAllOption={
              isAttendanceManager
            }
          />


          <div className="employee-attendance-filter-field">

            <label>
              From Date
            </label>

            <input
              type="date"
              value={
                filters.dateFrom
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      dateFrom:
                        event
                          .target
                          .value,
                    })
                  )
              }
            />

          </div>


          <div className="employee-attendance-filter-field">

            <label>
              To Date
            </label>

            <input
              type="date"
              value={
                filters.dateTo
              }
              min={
                filters.dateFrom ||
                undefined
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      dateTo:
                        event
                          .target
                          .value,
                    })
                  )
              }
            />

          </div>


          <div className="employee-attendance-filter-field">

            <label>
              Attendance Status
            </label>

            <select
              value={
                filters.status
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event
                          .target
                          .value,
                    })
                  )
              }
            >

              <option value="">
                All Status
              </option>

              <option value="PRESENT">
                Present
              </option>

              <option value="ABSENT">
                Absent
              </option>

              <option value="HALF_DAY">
                Half Day
              </option>

              <option value="PAID_LEAVE">
                Paid Leave
              </option>

              <option value="UNPAID_LEAVE">
                Unpaid Leave
              </option>

              <option value="HOLIDAY">
                Holiday
              </option>

              <option value="WEEK_OFF">
                Week Off
              </option>

            </select>

          </div>


          {
            isAttendanceManager
              ?
              (
                          <div className="employee-attendance-filter-field">

                            <label>
                              Search
                            </label>

                            <input
                              type="text"
                              value={
                                filters.search
                              }
                              onChange={
                                (
                                  event
                                ) =>
                                  setFilters(
                                    (
                                      previous
                                    ) => ({
                                      ...previous,

                                      search:
                                        event
                                          .target
                                          .value,
                                    })
                                  )
                              }
                              onKeyDown={
                                (
                                  event
                                ) => {

                                  if (
                                    event.key ===
                                    "Enter"
                                  ) {

                                    handleSearch();

                                  }

                                }
                              }
                              placeholder="Employee name/code"
                            />

                          </div>


              )
              :
              null
          }


          {
            !isAttendanceManager &&
            employees?.[0]
              ?
              (
                <div className="employee-attendance-self-scope-note">
                  <span>
                    Your attendance only
                  </span>

                  <strong>
                    {
                      employees?.[0]?.employeeCode ||
                      employees?.[0]?.name ||
                      "Current Employee"
                    }
                  </strong>
                </div>
              )
              :
              null
          }


          <div className="employee-attendance-filter-actions">

            <button
              type="button"
              className="employee-attendance-apply-btn"
              onClick={
                handleSearch
              }
            >
              Apply
            </button>


            <button
              type="button"
              className="employee-attendance-reset-btn"
              onClick={
                handleReset
              }
            >
              Reset
            </button>

          </div>

        </div>

      </section>


      {
        error
          ?
          (
            <div className="employee-attendance-error">
              {error}
            </div>
          )
          :
          null
      }


      <EmployeeAttendanceTable
        records={
          records
        }
        loading={
          loading
        }
        page={
          pagination.page
        }
        limit={
          pagination.limit
        }
        totalRecords={
          pagination.totalRecords
        }
        totalPages={
          pagination.totalPages
        }
        onPageChange={
          (
            page
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,

                page,
              })
            )
        }
        onLimitChange={
          (
            limit
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,

                page: 1,

                limit,
              })
            )
        }
        onView={
          (
            attendance
          ) => {

            setSelectedAttendance(
              attendance
            );


            setDetailsOpen(
              true
            );

          }
        }
        onDelete={
          isAttendanceManager
            ?
            (
              attendance
            ) => {

              setSelectedAttendance(
                attendance
              );


              setDeleteOpen(
                true
              );

            }
            :
            undefined
        }
      />


      {
        isAttendanceManager
          ?
          (
                  <BulkAttendanceModal
                    open={
                      bulkOpen
                    }
                    employees={
                      employees
                    }
                    onClose={
                      () =>
                        setBulkOpen(
                          false
                        )
                    }
                    onSuccess={
                      handleSuccess
                    }
                  />


          )
          :
          null
      }


      <AttendanceDetailsDrawer
        open={
          detailsOpen
        }
        attendance={
          selectedAttendance
        }
        onClose={
          () => {

            setDetailsOpen(
              false
            );


            setSelectedAttendance(
              null
            );

          }
        }
        onDelete={
          isAttendanceManager
            ?
            (
              attendance
            ) => {

              setDetailsOpen(
                false
              );


              setSelectedAttendance(
                attendance
              );


              setDeleteOpen(
                true
              );

            }
            :
            undefined
        }
      />


      {
        isAttendanceManager
          ?
          (
                  <EmployeeConfirmModal
                    open={
                      deleteOpen
                    }
                    title="Delete Attendance"
                    description="Delete this attendance record? This may affect salary calculation for the employee."
                    confirmLabel="Delete Attendance"
                    danger
                    loading={
                      actionLoading
                    }
                    onClose={
                      () => {

                        setDeleteOpen(
                          false
                        );


                        setSelectedAttendance(
                          null
                        );

                      }
                    }
                    onConfirm={
                      handleDelete
                    }
                  />

          )
          :
          null
      }


    </div>

  );

};


export default EmployeeAttendance;